import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Checkbox,
  Pagination,
  Loader,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconBrandTabler,
  IconEye,
} from "@tabler/icons-react";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import DeleteModal from "./DeleteModal";
import AddMerchandiseModal from "./AddMerchandiseModal";
import EditMerchandiseModal from "./EditMerchandiseModal";
import ViewMerchandiseModal from "./ViewMerchandiseModal";
import { RowDataMerchandise } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { merchandise } from "../types/RowDataTypes";
import { CustomTable } from "../Table/CustomTable";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import Header from "../static/Header";
import classes from "../MerchandisePage/merchandise.module.css";
import getMerchandiseTable from "../../DataDisplay/DataDisplayTable/MerchandiseTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  NAME = "Merchandise Name",
  MODEL = "Merchandise Model",
  IMAGE = "Image URL",
  QUANTITY = "Quantity",
  COST = "Cost",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.NAME]: "merch_name",
  [colFilter.MODEL]: "merch_model",
  [colFilter.IMAGE]: "",
  [colFilter.QUANTITY]: "quantity",
  [colFilter.COST]: "cost",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilter.QUANTITY.toLowerCase(),
    colFilter.COST.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text className={classes.Text}>{children}</Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataMerchandise[] = [
  {
    id: "",
    merchandiseName: "",
    merchandiseModel: "",
    merchandiseDesc: "",
    imageSrc: "",
    quantity: "",
    cost: "",
    action: "",
  },
];

export function Merchandise() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState("");
  const [tableData, setTableData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataMerchandise | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  //Used in searching and when deleting to cause rerender to fetch data
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.NAME],
    colFilters[colFilter.MODEL],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    merchandise,
    getMerchandiseTable().create()
  );
  const itemsOnPage = useRef(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  // const totalRows = fetchedData.totalRows;
  let rowData: RowDataMerchandise[] | null =
    fetchedData.data as RowDataMerchandise[];
//   const isLoading: boolean = fetchedData.isLoading;
  const [rowDataMerchandise, setRowDataMerchandise] = useState<
    RowDataMerchandise[] | null
  >(null);

  const [isAddMerchandiseModalOpen, setAddMerchandiseModalOpen] =
    useState(false);
  const [isEditMerchandiseModalOpen, setEditMerchandiseModalOpen] =
    useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] =
    useState<RowDataMerchandise | null>(null);
  const [isViewMerchandiseModalOpen, setViewMerchandiseModalOpen] =
    useState(false);
  const [selectedRowForView, setSelectedRowForView] =
    useState<RowDataMerchandise | null>(null);

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataMerchandise | null>(null);

  const handleAddMerchandise = () => {
    setAddMerchandiseModalOpen(true);
  };

  const handleAddMerchandiseModalClose = () => {
    setAddMerchandiseModalOpen(false);
  };

  const handleEdit = (row: RowDataMerchandise) => {
    setSelectedRowForEdit(row);
    setEditMerchandiseModalOpen(true);
  };

  const handleAddMerchandiseSuccess = (
    newMerchandiseData: RowDataMerchandise
  ) => {
    setIsLoading(true);
    axiosPrivate
      .post(
        "/api/admin/merchandise/add",
        {
          name: newMerchandiseData.merchandiseName,
          model: newMerchandiseData.merchandiseModel,
          desc: newMerchandiseData.merchandiseDesc,
          image: newMerchandiseData.imageSrc,
          quantity: newMerchandiseData.quantity,
          cost: Number.parseFloat(newMerchandiseData.cost).toFixed(2),
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then((response) => {
        // console.log(response);

        if (itemsOnPage.current < itemsPerPage) {
          const data = response.data.data;
          const returnedData: RowDataMerchandise = {
            ...newMerchandiseData,
            id: data.id,
            imageSrc: data.url,
          };
          if (!reverseSortDirection)
            setRowDataMerchandise((prevData) => [returnedData, ...prevData!!]);
          else
            setRowDataMerchandise((prevData) => [...prevData!!, returnedData]);
        }
        itemsOnPage.current = itemsOnPage.current + 1;
        setTotalRows((prevData) => prevData + 1);
        setIsLoading(false);
        showSuccessAlert("Merchandise Successfully Added");
      })
      .catch((error) => {
        // console.log("error " + error);
        setIsLoading(false);
        showErrorAlert("Something Went Wrong");
      });
  };

  const handleEditMerchandiseSuccess = (
    editedMerchandiseData: RowDataMerchandise
  ) => {
    setIsLoading(true);
    let params: any = {
      id: editedMerchandiseData.id,
      name: editedMerchandiseData.merchandiseName,
      model: editedMerchandiseData.merchandiseModel,
      desc: editedMerchandiseData.merchandiseDesc,
      quantity: editedMerchandiseData.quantity,
      cost: Number.parseFloat(editedMerchandiseData.cost).toFixed(2),
    };

    if (!!editedMerchandiseData.imageSrc.path)
      params["image"] = editedMerchandiseData.imageSrc;

    // console.log(params);

    axiosPrivate
      .post("/api/admin/merchandise/update", params, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // Find the index of the selectedRow in the sortedData
        const indexOfSelectedRow = rowDataMerchandise!!.findIndex(
          (row) => row === selectedRowForEdit
        );

        // Create a copy of the sortedData
        const updatedData = [...rowDataMerchandise!!];
        const data = response.data.links;

        let dataToUpdate: any = {
          ...selectedRowForEdit!!,
          merchandiseName: editedMerchandiseData.merchandiseName,
          merchandiseModel: editedMerchandiseData.merchandiseModel,
          merchandiseDesc: editedMerchandiseData.merchandiseDesc,
          quantity: editedMerchandiseData.quantity,
          cost: editedMerchandiseData.cost,
        };

        if (!!data) dataToUpdate.imageSrc = data;

        updatedData[indexOfSelectedRow] = dataToUpdate;
        // changeTableState();
        setRowDataMerchandise(updatedData);
        setIsLoading(false);
        showSuccessAlert("Merchandise Successfully Edited");
      })
      .catch((error) => {
        // console.log("error " + error);
        setIsLoading(false);
        showErrorAlert("Something Went Wrong");
      });
    setEditMerchandiseModalOpen(false);
  };

  const handleDelete = (row: RowDataMerchandise) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/merchandise/delete", {
        id: selectedRowToDelete!!.id,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        if (
          itemsOnPage.current == 1 &&
          currentPage !== 1 &&
          currentPage == Math.ceil(totalRows / itemsPerPage)
        )
          setCurrentPage((prev) => prev - 1);
        else changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });

    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleView = (row: RowDataMerchandise) => {
    setSelectedRowForView(row);
    setViewMerchandiseModalOpen(true);
  };

  const setSorting = (field: keyof RowDataMerchandise) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  useEffect(() => {
    setRowDataMerchandise(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const rowsToMap = rowDataMerchandise ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.merchandiseName}</Table.Td>
      <Table.Td className={classes.titleColumn}>
        {row.merchandiseModel}
      </Table.Td>
      <Table.Td className={classes.imageLink}>{row.imageSrc}</Table.Td>
      <Table.Td>{row.quantity}</Table.Td>
      <Table.Td>{row.cost}</Table.Td>

      <Table.Td className={classes.actionButtons}>
        <button onClick={() => handleView(row)} className={classes.viewButton}>
          <IconEye /> View
        </button>

        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>

        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.merchandisePageContainer}>
        {isLoading && (
        <div className={classes.loadingOverlay}>
          <Loader size="xl" />
        </div>
      )}
      <ScrollArea className={classes.parentContainer}>
        <Header title="Merchandise" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            <Button
              className={classes.addButton}
              onClick={handleAddMerchandise}
            >
              <IconBrandTabler className={classes.iconBrandTabler} />
              Create Merchandise
            </Button>
          </div>
        </div>

        <ViewMerchandiseModal
          isOpen={isViewMerchandiseModalOpen}
          onClose={() => setViewMerchandiseModalOpen(false)}
          merchandiseData={selectedRowForView || initialData[0]}
        />

        <AddMerchandiseModal
          isOpen={isAddMerchandiseModalOpen}
          onClose={handleAddMerchandiseModalClose}
          onAddMerchandise={handleAddMerchandiseSuccess}
        />

        <EditMerchandiseModal
          isOpen={isEditMerchandiseModalOpen}
          onClose={() => setEditMerchandiseModalOpen(false)}
          onEditMerchandise={handleEditMerchandiseSuccess}
          merchandiseData={selectedRowForEdit || initialData[0]}
        />

        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />
        <ScrollArea>
          <ViewMerchandiseModal
            isOpen={isViewMerchandiseModalOpen}
            onClose={() => setViewMerchandiseModalOpen(false)}
            merchandiseData={selectedRowForView || initialData[0]}
          />
        </ScrollArea>

        <ScrollArea
          w={
            window.innerWidth -
            document.getElementById("nav-bar")!!.getBoundingClientRect().width
          }
        >
          <CustomTable
            colHeaders={Object.values(ColumnHeaderToFilterPair)}
            filters={Object.values(colFilters)}
            reverseSortDirection={reverseSortDirection}
            sortBy={sortBy}
            setSorting={setSorting}
            customclasses={classes}
            Th={Th}
            rowData={rowDataMerchandise}
            rows={rows}
          />
        </ScrollArea>

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default Merchandise;
